import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  EyeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
} from '../../icons';
import { transcriptionAPI } from '../../services/api';
import JobStatusBadge from '../../components/transcription/JobStatusBadge';
import CaseReferenceLink from '../../components/cases/CaseReferenceLink';
import PageLoadingState from '../../components/layout/PageLoadingState';
import MatDashTableCard from '../../components/layout/MatDashTableCard';
import KpiStatCard from '../../components/layout/KpiStatCard';
import TableColumnToggle from '../../components/layout/TableColumnToggle';
import {
  parseTranscriptionRecord,
  formatDuration,
  formatDate,
} from '../../utils/transcriptionMeta';
import { normalizeJobStatus } from '../../utils/transcriptionStatus';
import { getLanguageLabel } from '../../utils/languages';

const COLUMN_DEFS = [
  { key: 'no', label: 'No.' },
  { key: 'title', label: 'Case title' },
  { key: 'case_ref', label: 'Case ref' },
  { key: 'date', label: 'Date' },
  { key: 'duration', label: 'Duration' },
  { key: 'language', label: 'Language' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions' },
];

const DEFAULT_VISIBLE = COLUMN_DEFS.map((c) => c.key);

const exportTranscriptionsCsv = (rows) => {
  const headers = ['Title', 'Case Reference', 'Date', 'Duration (s)', 'Language', 'Status'];
  const lines = rows.map((t) =>
    [
      t.title || '',
      t.case_reference || '',
      t.created_at || '',
      t.duration || '',
      getLanguageLabel(t.language) || '',
      t.status || '',
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(',')
  );
  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `proceedings-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const matchesDateRange = (createdAt, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  if (!createdAt) return false;
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return false;
  if (dateFrom && d < new Date(`${dateFrom}T00:00:00`)) return false;
  if (dateTo && d > new Date(`${dateTo}T23:59:59`)) return false;
  return true;
};

const AdminTranscriptions = () => {
  const navigate = useNavigate();
  const [transcriptions, setTranscriptions] = useState([]);
  const [allForKpi, setAllForKpi] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTranscriptions, setTotalTranscriptions] = useState(0);
  const itemsPerPage = 10;
  const [hasNextPage, setHasNextPage] = useState(false);
  const requestIdRef = useRef(0);
  const [fallbackMode, setFallbackMode] = useState(false);
  const [fallbackResults, setFallbackResults] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    if (!debouncedSearch) {
      setFallbackMode(false);
      setFallbackResults([]);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    transcriptionAPI
      .getTranscriptions({ page: 1, per_page: 500 })
      .then((resp) => {
        const list = resp.data?.data || resp.data || [];
        setAllForKpi(list.map(parseTranscriptionRecord));
      })
      .catch(() => setAllForKpi([]));
  }, []);

  const kpiStats = useMemo(() => {
    const list = allForKpi.length ? allForKpi : transcriptions;
    return {
      total: list.length,
      processing: list.filter((t) => {
        const s = normalizeJobStatus(t.status);
        return s === 'queued' || s === 'processing';
      }).length,
      complete: list.filter((t) => normalizeJobStatus(t.status) === 'complete').length,
      failed: list.filter((t) => normalizeJobStatus(t.status) === 'failed').length,
    };
  }, [allForKpi, transcriptions]);

  const extractTotal = (resp) =>
    resp?.data?.total ?? resp?.data?.pagination?.total ?? resp?.headers?.['x-total-count'] ?? null;

  useEffect(() => {
    if (!fallbackMode) return;
    const filtered = fallbackResults.filter((t) =>
      matchesDateRange(t.created_at, dateFrom, dateTo)
    );
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setTranscriptions(filtered.slice(start, end));
    setTotalTranscriptions(filtered.length);
    setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
    setHasNextPage(end < filtered.length);
  }, [fallbackMode, fallbackResults, currentPage, dateFrom, dateTo]);

  useEffect(() => {
    if (fallbackMode) return;
    const fetchTranscriptions = async () => {
      const myId = ++requestIdRef.current;
      try {
        if (pageLoading) setPageLoading(true);
        else setIsFetching(true);

        const params = { page: currentPage, per_page: itemsPerPage };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'all') params.status = statusFilter;

        const response = await transcriptionAPI.getTranscriptions(params);
        if (myId !== requestIdRef.current) return;

        const list = response.data?.data || response.data || [];
        let filtered = debouncedSearch
          ? list.filter((x) =>
              [x.title, x.filename, x.name, x.audio_filename]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())
            )
          : list;

        if (statusFilter !== 'all') {
          filtered = filtered.filter((x) => {
            const raw = x.status || x.state;
            if (statusFilter === 'queued') return normalizeJobStatus(raw) === 'queued';
            if (statusFilter === 'processing') return normalizeJobStatus(raw) === 'processing';
            return raw === statusFilter;
          });
        }

        filtered = filtered.filter((x) =>
          matchesDateRange(x.created_at, dateFrom, dateTo)
        );

        const processed = filtered.map((x) => parseTranscriptionRecord(x));

        if (debouncedSearch && processed.length === 0) {
          try {
            const allResp = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 1000 });
            const all = allResp.data?.data || allResp.data || [];
            const needle = debouncedSearch.toLowerCase();
            const filteredAll = all
              .filter((x) => {
                const matchesSearch = [x.title, x.filename, x.name, x.audio_filename]
                  .filter(Boolean)
                  .join(' ')
                  .toLowerCase()
                  .includes(needle);
                const matchesStatus =
                  statusFilter === 'all' || (x.status || x.state) === statusFilter;
                const matchesDate = matchesDateRange(x.created_at, dateFrom, dateTo);
                return matchesSearch && matchesStatus && matchesDate;
              })
              .map((x) => parseTranscriptionRecord(x));
            setFallbackMode(true);
            setFallbackResults(filteredAll);
            setIsFetching(false);
            setPageLoading(false);
            return;
          } catch {
            // fall through
          }
        }

        setTranscriptions(processed);
        let total = extractTotal(response);
        if (total == null && !debouncedSearch) {
          try {
            const probe = await transcriptionAPI.getTranscriptions({ page: 1, per_page: 1 });
            total = extractTotal(probe);
          } catch {
            // ignore
          }
        }
        if (total != null) {
          setTotalTranscriptions(total);
          setTotalPages(Math.max(1, Math.ceil(total / itemsPerPage)));
        } else {
          setTotalTranscriptions((currentPage - 1) * itemsPerPage + processed.length);
          setTotalPages(currentPage + (processed.length === itemsPerPage ? 1 : 0));
        }
        setHasNextPage(processed.length === itemsPerPage);
      } catch (e) {
        if (myId !== requestIdRef.current) return;
        setError(`Failed to load transcriptions: ${e.response?.data?.message || e.message}`);
        setTranscriptions([]);
        setTotalTranscriptions(0);
        setTotalPages(0);
        setHasNextPage(false);
      } finally {
        if (myId === requestIdRef.current) {
          setIsFetching(false);
          setPageLoading(false);
        }
      }
    };
    fetchTranscriptions();
  }, [currentPage, debouncedSearch, statusFilter, dateFrom, dateTo, fallbackMode]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 2) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const showCol = (key) => visibleColumns.includes(key);

  const openDetail = (id) => navigate(`/admin/transcriptions/${id}`);

  if (pageLoading) {
    return <PageLoadingState message="Loading proceedings…" />;
  }

  return (
    <>
      {error && <div className="md-alert md-alert-danger mb-4">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiStatCard label="Total transcriptions" value={kpiStats.total} icon={DocumentTextIcon} tone="primary" />
        <KpiStatCard label="Processing" value={kpiStats.processing} icon={ArrowPathIcon} tone="warning" />
        <KpiStatCard label="Completed" value={kpiStats.complete} icon={CheckCircleIcon} tone="success" />
        <KpiStatCard label="Failed" value={kpiStats.failed} icon={DocumentTextIcon} tone="danger" />
      </div>

      <div className="mb-4 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex-1 min-w-0 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-matdash-muted" />
          <input
            type="text"
            placeholder="Search transcripts…"
            value={searchTerm}
            onChange={handleSearchChange}
            className="md-form-control pl-10"
          />
        </div>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
          className="md-form-control w-full sm:w-36"
          title="From date"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
          className="md-form-control w-full sm:w-36"
          title="To date"
        />
        <select
          value={statusFilter}
          onChange={handleStatusChange}
          className="md-form-select w-full sm:w-40"
        >
          <option value="all">All status</option>
          <option value="queued">Queued</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <TableColumnToggle
          columns={COLUMN_DEFS.filter((c) => c.key !== 'actions')}
          visibleKeys={visibleColumns.filter((k) => k !== 'actions')}
          onToggle={toggleColumn}
        />
        <button
          type="button"
          onClick={() => exportTranscriptionsCsv(transcriptions)}
          disabled={transcriptions.length === 0}
          className="md-btn md-btn-outline md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>

      <MatDashTableCard
        title="Court transcriptions"
        subtitle={fallbackMode ? 'Showing expanded search results' : `${totalTranscriptions} transcription${totalTranscriptions !== 1 ? 's' : ''}`}
      >
        <div className="relative">
          {isFetching && (
            <div className="absolute inset-x-0 top-0 z-10 h-10 bg-white/80 backdrop-blur flex items-center justify-center border-b border-matdash-border">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-matdash-primary" />
              <span className="ml-2 text-xs text-matdash-muted">Updating…</span>
            </div>
          )}

          {transcriptions.length > 0 ? (
            <>
              <div className="block sm:hidden divide-y divide-matdash-border">
                {transcriptions.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => openDetail(t.id)}
                    className="block w-full text-left p-4 hover:bg-matdash-primary-subtle/30"
                  >
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-matdash-dark truncate">
                        {t.title || 'Untitled Transcription'}
                      </h3>
                      <JobStatusBadge status={t.status} className="text-xs shrink-0" />
                    </div>
                    {showCol('case_ref') && t.case_reference && (
                      <p className="text-xs text-matdash-muted mb-1">
                        Case: {t.case_reference}
                      </p>
                    )}
                    <div className="flex justify-between text-xs text-matdash-muted">
                      {showCol('date') && <span>{formatDate(t.created_at)}</span>}
                      {showCol('duration') && <span>{formatDuration(t.duration)}</span>}
                    </div>
                  </button>
                ))}
              </div>

              <div className="hidden sm:block md-table-wrap">
                <table className="md-table">
                  <thead>
                    <tr>
                      {showCol('no') && <th className="w-14 text-center">No.</th>}
                      {showCol('title') && <th>Case title</th>}
                      {showCol('case_ref') && <th>Case ref</th>}
                      {showCol('date') && <th>Date</th>}
                      {showCol('duration') && <th>Duration</th>}
                      {showCol('language') && <th>Language</th>}
                      {showCol('status') && <th>Status</th>}
                      {showCol('actions') && <th className="text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {transcriptions.map((t, index) => (
                      <tr
                        key={t.id}
                        onClick={() => openDetail(t.id)}
                        className="cursor-pointer hover:bg-matdash-primary-subtle/30"
                      >
                        {showCol('no') && (
                          <td className="text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-matdash-primary-subtle text-matdash-primary text-xs font-semibold">
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </span>
                          </td>
                        )}
                        {showCol('title') && (
                          <td className="font-medium">{t.title || 'Untitled Transcription'}</td>
                        )}
                        {showCol('case_ref') && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <CaseReferenceLink caseReference={t.case_reference} caseId={t.case_id} />
                          </td>
                        )}
                        {showCol('date') && (
                          <td className="text-matdash-muted">{formatDate(t.created_at)}</td>
                        )}
                        {showCol('duration') && (
                          <td className="text-matdash-muted">{formatDuration(t.duration)}</td>
                        )}
                        {showCol('language') && (
                          <td className="text-matdash-muted">{getLanguageLabel(t.language) || '—'}</td>
                        )}
                        {showCol('status') && (
                          <td><JobStatusBadge status={t.status} /></td>
                        )}
                        {showCol('actions') && (
                          <td className="text-right" onClick={(e) => e.stopPropagation()}>
                            <Link to={`/admin/transcriptions/${t.id}`} className="md-link inline-flex p-1" title="View">
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="md-empty-state">
              <DocumentTextIcon className="h-12 w-12 text-matdash-border mx-auto mb-3" />
              <h3 className="text-base font-semibold text-matdash-dark mb-1">No transcriptions found</h3>
              <p className="text-sm">
                {searchTerm || statusFilter !== 'all' || dateFrom || dateTo
                  ? 'Try adjusting your search or filter criteria.'
                  : 'Start by creating your first transcription from the dashboard.'}
              </p>
            </div>
          )}
        </div>
      </MatDashTableCard>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-matdash-muted text-center sm:text-left">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
          {(currentPage - 1) * itemsPerPage + transcriptions.length} of {totalTranscriptions} results
        </p>
        <div className="md-pagination">
          <button
            type="button"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="md-pagination-btn"
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <span className="px-2 text-sm font-medium text-matdash-dark">
            Page {currentPage}
            {totalPages ? ` of ${totalPages}` : ''}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={!hasNextPage}
            className="md-pagination-btn"
            aria-label="Next page"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminTranscriptions;
